import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { readStoredUser } from "../../utils/sessionUser";
import { hasPurchasingStockPermission } from "../../utils/purchasingStockPermissions";

// Results and pending exports stay attached to the account and hotel that requested them.
export default function usePurchasingReadAccess(businessUnitId) {
  const [user, setUser] = useState(readStoredUser);
  const expectedUserId = useRef(user?.id);
  const selectedUnit = useRef(businessUnitId);
  const mounted = useRef(true);
  selectedUnit.current = businessUnitId;
  useEffect(() => {
    mounted.current = true;
    const update = () => {
      const candidate = readStoredUser();
      setUser((current) => JSON.stringify(current) === JSON.stringify(candidate) ? current : candidate);
    };
    update();
    window.addEventListener("palantir-session-updated", update);
    window.addEventListener("storage", update);
    return () => {
      mounted.current = false;
      window.removeEventListener("palantir-session-updated", update);
      window.removeEventListener("storage", update);
    };
  }, [businessUnitId]);
  const can = useCallback((permission, current = false) => {
    const candidate = current ? readStoredUser() : user;
    return mounted.current && Boolean(candidate?.id) && String(candidate.id) === String(expectedUserId.current) &&
      Number.isSafeInteger(Number(businessUnitId)) && Number(businessUnitId) > 0 &&
      Number(candidate.active_business_unit_id) === Number(businessUnitId) &&
      Number(selectedUnit.current) === Number(businessUnitId) &&
      (Array.isArray(permission) ? permission : [permission]).every((key) => hasPurchasingStockPermission(candidate, key));
  }, [businessUnitId, user]);
  const requestConfig = useCallback((permission) => {
    if (!can(permission, true)) throw new Error("Your session or access changed. Reopen this report before continuing.");
    return { headers: { "X-Pilcore-Business-Unit-Id": String(businessUnitId) }, pilcoreExpectedUserId: expectedUserId.current, params: { business_unit_id: businessUnitId } };
  }, [businessUnitId, can]);
  const contextKey = JSON.stringify([user?.id, user?.active_business_unit_id, user?.permissions, businessUnitId]);
  return useMemo(() => ({ can, requestConfig, contextKey }), [can, requestConfig, contextKey]);
}
